import { useCallback, useEffect, useState } from 'react';
import { PlatformApiError, PlatformUnreachableError, type LivenessResponse, type ReadinessResponse } from '@sfc/sdk';
import { platform } from '../api/client';

export type StatusPhase = 'loading' | 'loaded' | 'unreachable';

export interface PlatformStatus {
  phase: StatusPhase;
  liveness?: LivenessResponse;
  readiness?: ReadinessResponse;
  error?: string;
  checkedAt?: Date;
}

const POLL_INTERVAL_MS = 10_000;

/**
 * Keeps the screen's picture of the backend current.
 *
 * "Unreachable" is kept distinct from "reachable but not ready": the first
 * means the API never answered, the second means it answered honestly that a
 * dependency is down. Collapsing them would hide which half is broken.
 */
export function usePlatformStatus(): PlatformStatus & { refresh: () => void } {
  const [status, setStatus] = useState<PlatformStatus>({ phase: 'loading' });

  const load = useCallback(async (signal: AbortSignal) => {
    try {
      const [liveness, readiness] = await Promise.all([
        platform.health.live(signal),
        platform.health.ready(signal),
      ]);
      setStatus({ phase: 'loaded', liveness, readiness, checkedAt: new Date() });
    } catch (error) {
      if (signal.aborted) return;
      setStatus({
        phase: 'unreachable',
        error: describe(error),
        checkedAt: new Date(),
      });
    }
  }, []);

  const [reloadToken, setReloadToken] = useState(0);
  const refresh = useCallback(() => setReloadToken((token) => token + 1), []);

  useEffect(() => {
    const controller = new AbortController();
    void load(controller.signal);
    const timer = setInterval(() => void load(controller.signal), POLL_INTERVAL_MS);
    return () => {
      controller.abort();
      clearInterval(timer);
    };
  }, [load, reloadToken]);

  return { ...status, refresh };
}

function describe(error: unknown): string {
  if (error instanceof PlatformUnreachableError) return 'The API did not answer.';
  if (error instanceof PlatformApiError) return `The API answered ${error.status} (${error.code}).`;
  return 'Something unexpected went wrong.';
}
